import { FormEvent, useState } from 'react'
import { createSubmission, type AssignmentSubmission } from '@/lib/submissions'
import type { CourseAssignment } from '@/lib/assignments'

interface QuizTakerProps {
  assignment: CourseAssignment
  courseId: string
  courseSlug: string
  moduleId: string
  userId: string
  studentName: string
  studentEmail: string
  onCreated: (record: AssignmentSubmission) => void
}

function QuizTaker({
  assignment,
  courseId,
  courseSlug,
  moduleId,
  userId,
  studentName,
  studentEmail,
  onCreated,
}: QuizTakerProps) {
  const questions = assignment.questions || []
  const [answers, setAnswers] = useState<number[]>(() => questions.map(() => -1))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const pick = (index: number, optionIndex: number) => {
    setAnswers((list) => list.map((value, i) => (i === index ? optionIndex : value)))
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (answers.some((value) => value === -1)) {
      setError('Responde todas las preguntas antes de enviar.')
      return
    }
    setError('')
    setBusy(true)
    try {
      const correctCount = questions.reduce(
        (total, item, index) => (item.correctIndex === answers[index] ? total + 1 : total),
        0,
      )
      const score = questions.length > 0 ? Math.round((correctCount / questions.length) * 100) : 0
      const record = await createSubmission({
        assignmentId: assignment._id,
        courseId,
        courseSlug,
        moduleId,
        userId,
        studentName,
        studentEmail,
        note: `Quiz respondido: ${correctCount}/${questions.length} correctas.`,
        fileUrl: '',
        fileName: '',
        score,
      })
      onCreated(record)
    } catch (err) {
      console.error('No se pudo enviar el quiz:', err)
      setError('No se pudo enviar el quiz. Inténtalo de nuevo.')
    } finally {
      setBusy(false)
    }
  }

  if (questions.length === 0) {
    return <p className="mt-4 text-sm text-[#1E3A5F]/60">Este quiz aún no tiene preguntas cargadas.</p>
  }

  return (
    <form onSubmit={onSubmit} className="mt-4 space-y-4 border border-dashed border-[#1E3A5F]/15 p-4">
      <p className="text-xs uppercase tracking-[0.18em] text-[#C41E3A]">Responde el quiz</p>
      {questions.map((item, index) => (
        <div key={index} className="space-y-2">
          <p className="text-sm font-medium">
            {index + 1}. {item.question}
          </p>
          <div className="grid gap-1.5 sm:grid-cols-3">
            {item.options.map((option, optionIndex) => (
              <label key={optionIndex} className="flex items-center gap-1.5 text-sm">
                <input
                  type="radio"
                  name={`quiz-${assignment._id}-${index}`}
                  checked={answers[index] === optionIndex}
                  onChange={() => pick(index, optionIndex)}
                />
                {option}
              </label>
            ))}
          </div>
        </div>
      ))}
      {error ? <p className="text-xs text-[#C41E3A]">{error}</p> : null}
      <button type="submit" disabled={busy} className="bg-[#1E3A5F] px-3 py-2 text-sm text-[#F4EFE6] disabled:opacity-50">
        {busy ? 'Enviando…' : 'Enviar respuestas'}
      </button>
    </form>
  )
}

export default QuizTaker
